import { cloneDeep, omit, pick } from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import { commitMutation, graphql } from 'react-relay';
import { Analytics, GlobalNotifications, Intent } from '../../Modules';
import Playlist from '../../Playlist';
import { Button, Modal } from 'semantic-ui-react';
import nullifyEmptyStrings from '../../../lib/utils/nullifyEmptyStrings';

export default class PlannerPlaylist extends React.Component {
  static propTypes = {
    params: PropTypes.object.isRequired,
    router: PropTypes.object.isRequired,
    node: PropTypes.object.isRequired,
    relay: PropTypes.object.isRequired,
    viewer: PropTypes.object
  };

  constructor(props) {
    super(props);
    this.state = {
      maxLengthWarningOpen: false
    };
    this.canEdit = this.canEdit.bind(this);
    this.goBack = this.goBack.bind(this);
    this.updatePlannerPlaylist = this.updatePlannerPlaylist.bind(this);
    this.updateMaxLength = this.updateMaxLength.bind(this);
    this.deletePlannerPlaylist = this.deletePlannerPlaylist.bind(this);
    this.deletePlannerPlaylistSong = this.deletePlannerPlaylistSong.bind(this);
    this.playlistContainsSong = this.playlistContainsSong.bind(this);
    this.createPlannerPlaylistSong = this.createPlannerPlaylistSong.bind(this);
    this.addPlannerPlaylistRecommendations = this.addPlannerPlaylistRecommendations.bind(
      this
    );
    this.removePlannerPlaylistRecommendation = this.removePlannerPlaylistRecommendation.bind(
      this
    );
  }

  goBack() {
    this.props.router.go(-1);
  }

  canEdit() {
    return (
      this.props.params.action === 'edit' &&
      this.props.node.music_planner.can_edit
    );
  }

  deletePlannerPlaylist(successCb) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to delete playlist',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      GlobalNotifications.show({
        message: 'Successfully deleted playlist',
        intent: Intent.SUCCESS
      });
      if (successCb) successCb();
      Analytics.trackEvent('deleted-planner-playlist');
    };

    const mutation = graphql`
      mutation PlannerPlaylist_deletePlannerPlaylistMutation(
        $input: DeletePlannerPlaylistInput!
      ) {
        deletePlannerPlaylist(input: $input) {
          deletedPlannerPlaylistId
        }
      }
    `;

    const environment = this.props.relay.environment;

    const configs = [
      {
        type: 'RANGE_DELETE',
        parentID: this.props.params.id,
        pathToConnection: ['music_planner', 'planner_playlists'],
        deletedIDFieldName: 'deletedPlannerPlaylistId',
        connectionKeys: [
          {
            key: 'MusicPlannerContainer_planner_playlists',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    commitMutation(environment, {
      mutation,
      variables: { input: { id: this.props.node.id } },
      onCompleted,
      onError,
      configs
    });
  }

  deletePlannerPlaylistSong(e, song, parent, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to delete song',
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      if (successCb) successCb();
    };

    const mutation = graphql`
      mutation PlannerPlaylist_deletePlannerPlaylistSongMutation(
        $input: DeletePlannerPlaylistSongInput!
      ) {
        deletePlannerPlaylistSong(input: $input) {
          deletedPlannerPlaylistSongId
          planner_playlist {
            thumbnail(width: 240, height: 240) {
              id
              width
              height
            }
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    const configs = [
      {
        type: 'RANGE_DELETE',
        parentID: this.props.node.id,
        pathToConnection: ['planner_playlist', 'playlist_songs'],
        deletedIDFieldName: 'deletedPlannerPlaylistSongId',
        connectionKeys: [
          {
            key: 'MusicPlannerContainer_planner_playlist_songs',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: parent.id
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  updatePlannerPlaylist(data, successCb, failureCb) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to update playlist',
        intent: Intent.DANGER
      });
      if (failureCb) failureCb();
    };
    const onCompleted = () => {
      if (successCb) successCb();
      Analytics.trackEvent('updated-planner-playlist');
    };

    const mutation = graphql`
      mutation PlannerPlaylist_UpdatePlannerPlaylistMutation(
        $input: UpdatePlannerPlaylistInput!
      ) {
        updatePlannerPlaylist(input: $input) {
          planner_playlist {
            id
            title
            description
            position
            max_length
          }
        }
      }
    `;

    const optimisticResponse = {
      updatePlannerPlaylist: {
        planner_playlist: {
          ...this.props.node,
          [data.name]: data.value
        }
      }
    };

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.props.node.id,
          [data.name]: data.value
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  updateMaxLength(data, successCb, failureCb) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to update playlist',
        intent: Intent.DANGER
      });
      if (failureCb) failureCb();
    };
    const onCompleted = () => {
      if (successCb) successCb();
      Analytics.trackEvent('updated-planner-playlist');
    };

    const mutation = graphql`
      mutation PlannerPlaylist_MaxLength_UpdatePlannerPlaylistMutation(
        $input: UpdatePlannerPlaylistInput!
      ) {
        updatePlannerPlaylist(input: $input) {
          planner_playlist {
            id
            max_length
          }
        }
      }
    `;

    const maxLength = data.value ? parseInt(data.value, 0) : null;

    const optimisticResponse = {
      updatePlannerPlaylist: {
        planner_playlist: {
          ...this.props.node,
          maxLength
        }
      }
    };

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          id: this.props.node.id,
          max_length: maxLength
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  createPlannerPlaylistSong(e, song, parent, successCb, failureCb) {
    if (Array.isArray(song)) {
      this.createPlannerPlaylistSongs(e, song, parent, successCb, failureCb);
      return;
    }
    const maxLength = this.props.node.max_length;
    if (maxLength && this.props.node.playlist_songs.edges.length >= maxLength) {
      if (failureCb) failureCb();
      this.setState({ maxLengthWarningOpen: true });
      return;
    }
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add song to playlist',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
    };

    const mutation = graphql`
      mutation PlannerPlaylist_createPlannerPlaylistSongMutation(
        $input: CreatePlannerPlaylistSongInput!
      ) {
        createPlannerPlaylistSong(input: $input) {
          new_planner_playlist_song_edge {
            node {
              id
              position
              song {
                id
                isrc
                name
                artist
                album
                song_images {
                  edges {
                    node {
                      url
                      width
                      height
                    }
                  }
                }
              }
            }
          }
          planner_playlist {
            thumbnail(width: 240, height: 240) {
              id
              width
              height
            }
          }
        }
      }
    `;

    const songParams = nullifyEmptyStrings(omit(song, 'id', '__typename'));
    if (songParams.external_source) {
      songParams.external_source = songParams.external_source.toUpperCase();
    }

    const parentID = this.props.node.id;

    const configs = [
      {
        type: 'RANGE_ADD',
        parentID,
        edgeName: 'new_planner_playlist_song_edge',
        connectionInfo: [
          {
            key: 'MusicPlannerContainer_planner_playlist_songs',
            rangeBehavior: 'append'
          }
        ]
      }
    ];

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          planner_playlist_id: parentID,
          ...songParams,
          song_images: songParams.song_images
            ? songParams.song_images.edges.map(({ node: image }) =>
                pick(image, 'url', 'width', 'height')
              )
            : []
        }
      },
      configs,
      onCompleted,
      onError
    });
  }

  createPlannerPlaylistSongs(e, songs, parent, successCb, failureCb) {
    const maxLength = this.props.node.max_length;
    if (
      maxLength &&
      this.props.node.playlist_songs.edges.length + songs.length > maxLength
    ) {
      if (failureCb) failureCb();
      const songsLeft = maxLength - this.props.node.playlist_songs.edges.length;
      this.setState({
        maxLengthWarningOpen: true,
        maxLengthMessage:
          songsLeft > 0
            ? `Please select up to ${songsLeft} song${songsLeft !== 1
                ? 's'
                : ''}.`
            : null
      });
      return;
    }
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to add songs to playlist',
        intent: Intent.DANGER
      });
    };
    const onCompleted = () => {
      if (successCb) successCb();
    };

    const mutation = graphql`
      mutation PlannerPlaylist_createPlannerPlaylistSongsMutation(
        $input: CreatePlannerPlaylistSongsInput!
      ) {
        createPlannerPlaylistSongs(input: $input) {
          planner_playlist {
            ...PlannerPlaylistContainer_node
          }
        }
      }
    `;

    const newSongs = songs.map(song => {
      const newSong = nullifyEmptyStrings(omit(song, 'id', '__typename'));
      if (newSong.external_source) {
        newSong.external_source = newSong.external_source.toUpperCase();
      }
      newSong.song_images = newSong.song_images
        ? newSong.song_images.edges.map(({ node: image }) =>
            pick(image, 'url', 'width', 'height')
          )
        : [];
      return newSong;
    });

    const parentID = this.props.node.id;

    const environment = this.props.relay.environment;

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          planner_playlist_id: parentID,
          songs: newSongs
        }
      },
      onCompleted,
      onError
    });
  }

  playlistContainsSong(song) {
    for (const playlistSong of this.props.node.playlist_songs.edges) {
      if (playlistSong.node.song.isrc === song.isrc) return true;
    }
    return false;
  }

  addPlannerPlaylistRecommendations(playlistIds, successCb, failureCb) {
    const onError = () => {
      GlobalNotifications.show({
        message: 'Unable to add playlist recommendations',
        intent: Intent.DANGER
      });
      if (failureCb) failureCb();
    };
    const onCompleted = () => {
      if (successCb) successCb();
      Analytics.trackEvent('added-planner-playlist-recommendations');
    };

    const mutation = graphql`
      mutation PlannerPlaylist_addPlannerPlaylistRecommendationsMutation(
        $input: AddPlannerPlaylistRecommendationsInput!
      ) {
        addPlannerPlaylistRecommendations(input: $input) {
          planner_playlist {
            ...PlannerPlaylistContainer_node
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    const allPlaylists = this.props.viewer.playlists.edges.concat(
      this.props.viewer.public_playlists.edges
    );
    const newRecs = playlistIds.map(
      id => allPlaylists.filter(p => p.node.id === id)[0]
    );
    const plannerPlaylist = cloneDeep(this.props.node);
    plannerPlaylist.playlist_recommendations.edges = plannerPlaylist.playlist_recommendations.edges.concat(
      newRecs
    );

    const optimisticResponse = {
      addPlannerPlaylistRecommendations: {
        planner_playlist: plannerPlaylist
      }
    };

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          planner_playlist_id: this.props.node.id,
          playlist_ids: playlistIds
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  removePlannerPlaylistRecommendation(playlistId, successCb, failureCb) {
    const onError = () => {
      if (failureCb) failureCb();
      GlobalNotifications.show({
        message: 'Unable to remove playlist recommendation',
        intent: Intent.DANGER
      });
    };

    const onCompleted = () => {
      if (successCb) successCb();
    };

    const mutation = graphql`
      mutation PlannerPlaylist_removePlannerPlaylistRecommendationMutation(
        $input: RemovePlannerPlaylistRecommendationInput!
      ) {
        removePlannerPlaylistRecommendation(input: $input) {
          planner_playlist {
            ...PlannerPlaylistContainer_node
          }
        }
      }
    `;

    const environment = this.props.relay.environment;

    const plannerPlaylist = cloneDeep(this.props.node);
    plannerPlaylist.playlist_recommendations.edges = plannerPlaylist.playlist_recommendations.edges.filter(
      p => p.node.id !== playlistId
    );

    const optimisticResponse = {
      removePlannerPlaylistRecommendation: {
        planner_playlist: plannerPlaylist
      }
    };

    commitMutation(environment, {
      mutation,
      variables: {
        input: {
          planner_playlist_id: this.props.node.id,
          playlist_id: playlistId
        }
      },
      optimisticResponse,
      onCompleted,
      onError
    });
  }

  render() {
    const { node, relay, viewer } = this.props;
    const { maxLengthMessage, maxLengthWarningOpen } = this.state;
    return (
      <div style={{ paddingTop: '1em', paddingBottom: '3em' }}>
        <Modal
          open={maxLengthWarningOpen}
          onClose={() =>
            this.setState({
              maxLengthWarningOpen: false,
              maxLengthMessage: null
            })}
          size='mini'
        >
          <Modal.Header content='Song Limit Reached' />
          <Modal.Content>
            <p>
              You can only add up to {' '}
              <strong>
                {node.max_length} {`song${node.max_length !== 1 ? 's' : ''}`}
              </strong>{' '}
              to this playlist.{' '}
              {maxLengthMessage ||
                'Please remove an existing song before adding a new one.'}
            </p>
          </Modal.Content>
          <Modal.Actions>
            <Button
              content='Close'
              onClick={() => this.setState({ maxLengthWarningOpen: false })}
            />
          </Modal.Actions>
        </Modal>
        <Playlist
          playlist={node}
          goBack={this.goBack}
          relay={relay}
          searchResults={node.song_search.edges.map(edge => edge.node)}
          refetchSearchResults={relay.refetch}
          viewer={viewer}
          editable={this.canEdit()}
          fillable
          showControls={this.canEdit()}
          addPlaylistRecommendations={this.addPlannerPlaylistRecommendations}
          removePlaylistRecommendation={
            this.removePlannerPlaylistRecommendation
          }
          updatePlaylist={this.updatePlannerPlaylist}
          updateMaxLength={this.updateMaxLength}
          deletePlaylist={this.deletePlannerPlaylist}
          removeFromPlaylist={this.deletePlannerPlaylistSong}
          playlistContainsSong={this.playlistContainsSong}
          addToPlaylist={this.createPlannerPlaylistSong}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./webpack/components/Document/MusicPlanner/PlannerPlaylist.js